/**
 * Redeems a staff-issued portal invite (`POST /api/owners/:id/portal-invite`
 * / `.../tenants/:id/portal-invite` / `.../vendors/:id/portal-invite`) —
 * the landing page for the link an owner, tenant, or vendor gets emailed.
 * Structured like `ApplicantSignUp.tsx`: check the token on mount, then a
 * minimal form. Unlike that flow, there's no name/email to collect — both
 * come from the owner/tenant/vendor record the invite was issued against,
 * not re-typed here.
 *
 * On success the API sets the session cookie directly (same as
 * applicant-sign-up), so this only has to `refresh()` and land on `/portal`,
 * which already branches by actor type.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { PortalInviteStatusResponse } from 'shared';
import { api, ApiError } from '../../lib/api.js';
import { useSession } from '../../lib/session.js';
import { Field, FormErrors, PasswordInput, fieldErrors } from '../../components/Form.js';

const REASON_MESSAGE: Record<NonNullable<PortalInviteStatusResponse['reason']>, string> = {
  NOT_FOUND: 'This invite link is not valid.',
  EXPIRED: 'This invite has expired.',
  CONSUMED: 'This invite has already been used.',
};

export function RedeemInvite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refresh } = useSession();

  const [status, setStatus] = useState<PortalInviteStatusResponse | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!token) return;
    void api<PortalInviteStatusResponse>(`/portal-invites/${token}`)
      .then(setStatus)
      .catch(() => setStatus({ valid: false }));
  }, [token]);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError(new ApiError(422, 'VALIDATION', 'Passwords do not match.'));
      return;
    }

    setBusy(true);
    try {
      await api(`/portal-invites/${token}/redeem`, {
        method: 'POST',
        body: JSON.stringify({ password }),
      });
      await refresh();
      void navigate('/portal', { replace: true });
    } catch (caught) {
      setError(caught);
      setBusy(false);
    }
  }

  const errors = fieldErrors(error);

  if (!status) {
    return (
      <div className="auth-page">
        <div className="card card--auth card--auth--portal">
          <p className="muted">Checking your invite…</p>
        </div>
      </div>
    );
  }

  if (!status.valid) {
    return (
      <div className="auth-page">
        <div className="card card--auth card--auth--portal">
          <h1>Invite not valid</h1>
          <p className="alert alert--error">{REASON_MESSAGE[status.reason ?? 'NOT_FOUND']}</p>
          <p className="muted">Contact the management office for a new invite.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="card card--auth card--auth--portal">
        <h1>Welcome, {status.recipientName}</h1>
        <p className="muted">Choose a password to finish setting up your portal login.</p>

        <FormErrors error={error} />

        <form className="stack" onSubmit={(event) => void onSubmit(event)} noValidate>
          <Field
            label="Password"
            name="password"
            error={errors.password}
            hint="At least 12 characters."
            required
          >
            <PasswordInput
              name="password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
            />
          </Field>
          <Field label="Confirm password" name="confirm" required>
            <PasswordInput
              name="confirm"
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
            />
          </Field>

          <button className="button" type="submit" disabled={busy}>
            {busy ? 'Setting up…' : 'Set up my account'}
          </button>
        </form>
      </div>
    </div>
  );
}
